import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import Grid from '@mui/material/Grid'
import LoadingButton from '@mui/lab/LoadingButton'
import Paper from '@mui/material/Paper'
import TableContainer from '@mui/material/TableContainer'
import Stack from '@mui/material/Stack'
import ClearIcon from '@mui/icons-material/Clear'

import PageContainer from '@/components/ui/containers/PageContainer'
import Pagination from '@/components/ui/pagination/Pagination'
import TextInput from '@/components/ui/inputs/TextInput'

import AddMedecinTable from './components/AddMedecinTable'
import CreateStaffForm, { CreateStaffFormValues } from './components/CreateStaffForm'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import {
  getMedecins,
  selectMedecinListRequestTracker,
  selectMedecins,
  selectMedecinSearchParams,
  selectMedecinSearchResults,
} from '@/redux/reducers/medecin.reducer'
import { CreateStaffDto } from '@/core/models/staff.model'
import { useIsSA } from '@/hooks/useIsSA'
import { useRequestHandler } from '@/hooks/useRequestHandler'
import { useSnackbar } from 'notistack'
import { KeycloakUserType } from '@/core/types/shared'
import { SubscriptionType } from '@/utils/subscription/types'
import { addStaff, selectAdminCreateRequestTracker } from '@/redux/reducers/admin.reducer'

const rowsPerPageOptions = [5, 10, 15]
type oldCreatestaffDto = {
  name: string
  surname: string
  cps?: string
  email: string
  status: 'MEDECIN' | 'PDS'
  active?: boolean
  stripe?: string
  phone?: string
  subscription?: SubscriptionType
}
// TODO: rename component page
export function CreateStaffPage() {
  const dispatch = useAppDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const addMedecinRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.medecins.create.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.medecins.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectAdminCreateRequestTracker
  })
  const medecinSearchParams = useAppSelector(selectMedecinSearchParams)
  const medecinSearchResult = useAppSelector(selectMedecinSearchResults)
  const medecinListRequestTracker = useAppSelector(selectMedecinListRequestTracker)

  const { t } = useTranslation()
  const isSA = useIsSA()

  const [page, setPage] = useState<number>(1)

  const [itemsPerPage, setItemsPerPage] = useState<number>(rowsPerPageOptions[0])
  const [initialValues, setInitialValues] = useState<CreateStaffFormValues>({ name: '', surname: '', cps: '', email: '', status: 'PDS' })

  const [openDialog, setOpenDialog] = useState<boolean>(false)

  const medecins = useAppSelector(selectMedecins)
  useEffect(() => {
    const options = {
      limit: 5,
      skip: 0,
      orderBy: 'name',
    }
    dispatch(getMedecins({ surname: '1000' }, options))
  }, [dispatch])

  const debouncedOnChange = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const textSearch = event.target.value
    const query =
      textSearch === ''
        ? { surname: '100' }
        : {
          surname: textSearch,
        }
    const options = {
      limit: 5,
      skip: 0,
      orderBy: 'name',
    }
    dispatch(getMedecins(query, options))
  }, 300)

  const handleToggleDialog = () => {
    setOpenDialog(!openDialog)
  }

  const transformData = (values: CreateStaffFormValues): CreateStaffDto => {
    let data = _.pickBy<CreateStaffFormValues>(values, (value) => !_.isEmpty(value)) as oldCreatestaffDto
    data.cps = data.cps !== "" ? data.cps : undefined
    return {
      cps: data.cps !== "" ? data.cps : undefined,
      status: data.status,
      email: data.email,
      userInfo: {
        name: data.name,
        surname: data.surname
      },
      type: KeycloakUserType.STAFF,
      contactInfo: {
        phone: data.phone
      },
      // SEHA (en-SA): persist the specialty, and only for Doctor accounts.
      // Other locales keep the historical behavior (the field is not sent).
      speciality: isSA && values.status === 'MEDECIN' && values.speciality ? values.speciality : undefined,
    }
  }

  const handleStaffSubmit = (values: CreateStaffFormValues) => {
    handleToggleDialog()
    addMedecinRequestHandler.dispatch(
      addStaff(transformData(values))
    )
  }

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    dispatch(getMedecins(medecinSearchParams.query, { limit: itemsPerPage, skip: (newPage - 1) * itemsPerPage }))
  }

  const handleChangeItemsPerPage = (newItemsPerPage: number) => {
    if (itemsPerPage === newItemsPerPage) return
    setPage(1)
    setItemsPerPage(newItemsPerPage)
    dispatch(getMedecins(medecinSearchParams.query, { limit: newItemsPerPage }))
  }

  const handleStaffCreation = (data: any) => {
    setInitialValues(data)
    setOpenDialog(true)
  }

  return (
    <PageContainer>
      <Grid container spacing={2}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <LoadingButton loading={addMedecinRequestHandler.requestTracker.loading} onClick={() => handleStaffCreation(initialValues)} variant="contained" id="add-staff-button">
                {t('page.admin.addStaff.addStaffButton')}
              </LoadingButton>
            </Stack>
          </Grid>
          <Grid item xs={12} lg={8} xl={9}>
            <TextInput defaultValue={''} fullWidth onChange={debouncedOnChange} placeholder={t('page.medecins.searchInput.placeholder')} />
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={4}>
          <Grid item xs={12}>
            {
              <TableContainer component={Paper}>
                <AddMedecinTable onAdd={handleStaffCreation} data={medecins} autoHightlight={true} loading={medecinListRequestTracker.loading} />
                <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                  <Pagination
                    color="primary"
                    itemsPerPageOptions={rowsPerPageOptions}
                    shape="rounded"
                    page={page}
                    onPageChange={handleChangePage}
                    onItemsPerPageChange={handleChangeItemsPerPage}
                    totalItems={_.get(medecinSearchResult, 'totalCount', 0)}
                  />
                </Box>
              </TableContainer>
            }
          </Grid>
        </Grid>
        {/** TODO: extract to a component */}
        <Dialog open={openDialog} onClose={handleToggleDialog}>
          <LoadingButton onClick={handleToggleDialog} sx={{ position: 'absolute', top: '5px', right: '5px' }} data-testid="clear-button">
            <ClearIcon />
          </LoadingButton>
          <Paper sx={{ mt: '15px', p: '30px' }}>
            <CreateStaffForm initialValues={initialValues} onSubmit={handleStaffSubmit} onCancel={handleToggleDialog} />
          </Paper>
        </Dialog>
      </Grid>
    </PageContainer>
  )
}

export default CreateStaffPage
